import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLoadYesterdayLinks } from "../../../../Utils/customHooks/UpToDateContent/useLoadYesterdayLinks";
import {
  handleFirstImage,
  handleNavigate,
  updateIsEmptyStatus,
} from "../../../../Utils/StayUpToDate/RenderComponent";
import { StayFeedLoadingPlaceholder } from "../../../../Screens/DashboardView/MasterTemplateEditor/WebsiteLibFeedSkeleton";
import { useParams } from "react-router-dom";
import { debounce } from "lodash";
import "./YesterdayLinks.css";
import { StaticVariables } from "../../../../Utils/StaticVariables";
import FrontendTemplate from "../StructureTemplates/FrontendTemplate";
import PostTemplate from "../CommonComponents/PostTemplate";
import { FeedSectionEmptyState } from "../FeedSectionEmptyState";
import { websiteEditorFeedEmptyMessage } from "../../../../Screens/DashboardView/MasterTemplateEditor/websiteEditorFeedEmptyMessage";
import { addFeedScrollListener, getFeedScrollMetrics } from "../../../../Utils/feedScrollRoot";

const YesterdayLinksLoader = (props: any) => {
  const itemsPerPage = 3;
  const {
    user_id,
    username,
    numberOfDays,
    title,
    isLinks,
    handleRedirect,
    postIds,
    brand_color,
    removedPostIds,
    isHandleNavigate,
    component,
    type,
    RenderUI,
    componentType,
    from,
    designTokens,
    templateGridLoaderProps,
    expandedSlot,
    gridSkeletonColumnCount = 8,
    isMobileView,
    onFeedResolved,
    showEmptyInEditor = false,
  } = props;
  const param = useParams();
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<any>(null);
  const isButtonClicked = useRef(false);
  const displayNameSegments = useMemo(() => {
    return component?.display_name ? component.display_name.split(",") : undefined;
  }, [component?.display_name]);

  const customOrderSegments = useMemo(() => {
    return component?.custom_order ? component.custom_order.split(",") : undefined;
  }, [component?.custom_order]);

  const contentOrder = component?.content_order;

  // Prefer daywise / display_name so media routing skips product stills even when
  // component.type is blank (common for "This Week" / "Shop This Past Week").
  const mediaComponentType = String(
    componentType ||
      component?.type ||
      component?.display_name ||
      type ||
      "",
  );

  // Honor DB shoppable_content_filter. Hardcoding "products" (eaa609071e) made
  // content day-windows like ninatakesh "Recently Worn & Mentioned"
  // (This Month + filter=content) load only LTK/ShopMy/Amazon.
  const activeFilter = useMemo<"both" | "content" | "products">(() => {
    const f = String(component?.shoppable_content_filter ?? "")
      .toLowerCase()
      .trim();
    if (f === "content") return "content";
    if (f === "products") return "products";
    return "both";
  }, [component?.shoppable_content_filter]);

  const {
    isLoading: yesterdayLinksLoading,
    data: yesterdayLinksData,
    total_count: totalCount,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  }: any = useLoadYesterdayLinks(
      username,
      user_id,
      numberOfDays,
      postIds,
      removedPostIds,
      title,
      false,
      contentOrder,
      displayNameSegments,
      componentType,
      customOrderSegments,
      activeFilter // Pass activeFilter so SQL can filter by platform
    );

  // Local gate until handleFirstImage / empty check finishes (isLoading from hook = initial fetch only)
  const [isLoading, setIsLoading] = useState(() => yesterdayLinksLoading);

  // get_shoplink_content has no shoppable_status_filter — mirror ghost/live filter client-side.
  const rawData = useMemo(() => {
    return (yesterdayLinksData || []).filter(
      (element: any) => element !== undefined,
    );
  }, [yesterdayLinksData]);

  const filteredData = useMemo(() => {
    const status = String(component?.shoppable_status_filter || "all")
      .toLowerCase()
      .trim();
    if (status === "all" || !Array.isArray(rawData)) return rawData;
    return rawData.filter((post: any) => {
      const link =
        typeof post?.shop_link === "string"
          ? post.shop_link.trim()
          : typeof post?.shopLink === "string"
            ? post.shopLink.trim()
            : typeof post?.shop_url === "string"
              ? post.shop_url.trim()
              : typeof post?.product_url === "string"
                ? post.product_url.trim()
                : "";
      const shoppable =
        post?.is_shoppable === true ||
        post?.isShoppable === true ||
        Boolean(link);
      return status === "shoppable" ? shoppable : !shoppable;
    });
  }, [rawData, component?.shoppable_status_filter]);

  useEffect(() => {
    const checkEmptyStatus = async () => {
      // Only check empty status on initial load, not when fetching next page.
      if (!yesterdayLinksLoading && !isFetchingNextPage) {
        if (!filteredData || filteredData.length === 0) {
          // Editor keeps the section visible with a "No data" placeholder and may
          // be filtering live before Update — don't mark the row empty in DB here.
          if (!showEmptyInEditor) {
            await updateIsEmptyStatus(title, user_id, component.id, from);
          }
          setIsLoading(false);
        } else {
          await handleFirstImage(
            title,
            component?.id,
            filteredData[0],
            user_id,
            from
          );
          setIsLoading(false);
        }
      }
    };
    checkEmptyStatus();
  }, [
    yesterdayLinksLoading,
    filteredData,
    user_id,
    component?.id,
    title,
    isFetchingNextPage,
    from,
    showEmptyInEditor,
  ]);

  const handlePrevPage = () => {
    isButtonClicked.current = true;
    setCurrentIndex((prevIndex) => Math.max(prevIndex - 1, 0));
    setTimeout(() => {
      isButtonClicked.current = false;
    }, 500);
  };

  const handleNextPage = () => {
    isButtonClicked.current = true;
    setCurrentIndex((prevIndex) =>
      Math.min(prevIndex + 1, filteredData.length - itemsPerPage)
    );
    setTimeout(() => {
      isButtonClicked.current = false;
    }, 500);
  };

  const handleScroll = useCallback(
    debounce(() => {
      if (!isButtonClicked.current) {
        if (containerRef.current) {
          const scrollLeft = containerRef.current.scrollLeft;
          const itemWidth =
            containerRef.current.scrollWidth / filteredData.length;
          const newCurrentIndex = Math.round(scrollLeft / itemWidth);
          setCurrentIndex(newCurrentIndex);
        }
      }
    }, 100),
    [filteredData]
  );

  useEffect(() => {
    if (containerRef.current) {
      const itemWidth = containerRef.current.scrollWidth / filteredData.length;
      const scrollPosition = itemWidth * currentIndex;
      containerRef.current.scrollTo({
        left: scrollPosition,
        behavior: "smooth",
      });
    }
  }, [currentIndex, filteredData]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.addEventListener("scroll", handleScroll);
      return () => {
        if (containerRef.current) {
          containerRef.current.removeEventListener("scroll", handleScroll);
        }
      };
    }
  }, [filteredData, handleScroll]);

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) return;

    let ticking = false;

    const handleWindowScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const { scrollPercentage } = getFeedScrollMetrics();

          if (scrollPercentage >= 70) {
            if (hasNextPage && !isFetchingNextPage) {
              fetchNextPage();
            }
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    return addFeedScrollListener(handleWindowScroll);
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Auto-fetch next page when scrolling near the end (similar to QueryLinkDataLoader)
  // Increased buffer to fetch earlier for smoother experience
  useEffect(() => {
    if (
      typeof fetchNextPage === "function" &&
      hasNextPage &&
      !isFetchingNextPage &&
      filteredData &&
      filteredData.length > 0
    ) {
      // Increased buffer to 9 (3x itemsPerPage) to trigger fetching much earlier
      const buffer = itemsPerPage * 3;
      const itemsLeft = filteredData.length - (currentIndex + buffer);
      if (itemsLeft <= buffer) {
        fetchNextPage();
      }
    }
  }, [
    currentIndex,
    filteredData?.length,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    itemsPerPage,
  ]);

  useEffect(() => {
    if (from !== "website-template" || !onFeedResolved) return;
    if (yesterdayLinksLoading || isLoading) return;
    onFeedResolved(filteredData.length > 0);
  }, [
    from,
    onFeedResolved,
    yesterdayLinksLoading,
    isLoading,
    filteredData.length,
  ]);

  return (
    <>
      {(yesterdayLinksLoading && !isFetchingNextPage) || isLoading ? (
        <>
<StayFeedLoadingPlaceholder
            arrayLength={RenderUI ? gridSkeletonColumnCount : 5}
            individualGrid={Boolean(RenderUI) && !expandedSlot}
            expandedSlot={expandedSlot}
            from={from}
            component={component}
            designTokens={designTokens}
            templateGridLoaderProps={templateGridLoaderProps}
          />

        </>
      ) : filteredData.length === 0 ? (
        showEmptyInEditor ? (
          <FeedSectionEmptyState
            title={title}
            designTokens={designTokens}
            message={websiteEditorFeedEmptyMessage("products")}
          />
        ) : null
      ) : (
        <>
          {RenderUI ? (
            <RenderUI
              data={filteredData}
              currentIndex={currentIndex}
              handleNavigate={handleNavigate}
              component={component}
              handleNextPage={handleNextPage}
              handlePrevPage={handlePrevPage}
              containerRef={containerRef}
              handleRedirect={handleRedirect}
              brand_color={brand_color}
              title={title}
              type={type}
              componentType={mediaComponentType}
              isLinks={isLinks}
              from={from}
              username={username}
              user_id={user_id}
              designTokens={designTokens}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              expandedSlot={expandedSlot}
              isMobileView={isMobileView}
            />
          ) : filteredData && filteredData.length > 0 ? (
            <PostTemplate
              processedData={filteredData}
              total_count={totalCount ?? 0}
                component={component}
                user_id={user_id}
                username={username}
              handleRedirect={handleRedirect}
              brand_color={brand_color}
              title={title}
                from={from}
              type={mediaComponentType}
              itemsPerPage={itemsPerPage}
              pinnedPostIds={component?.pinned_posts?.split(",") || []}
              isLinks={isLinks}
              designTokens={designTokens}
              />
          ) : (
            ""
          )}
        </>
      )}
    </>
  );
};

export default YesterdayLinksLoader;
